import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, Eye, EyeSlash } from "@phosphor-icons/react";
import { PageHeader, Card, timeAgo } from "./_shared";

const EMPTY = { make: "", model: "", variant: "", year: "", location: "", note: "", acquired_on: "", image_url: "" };

export default function Acquisitions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/acquisitions");
      setItems(data || []);
    } catch (err) {
      console.error("Acquisitions load failed:", err);
      toast.error("Failed to load acquisitions");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const create = async () => {
    if (!form.make.trim() || !form.model.trim()) {
      toast.error("Make and Model required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: form.year ? Number(form.year) : null,
        acquired_on: form.acquired_on || null,
      };
      await api.post("/admin/acquisitions", payload);
      toast.success("Acquisition published");
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const togglePublish = async (a) => {
    try {
      await api.patch(`/admin/acquisitions/${a.id}/publish?published=${!a.published}`);
      load();
    } catch { toast.error("Failed to update"); }
  };

  const remove = async (a) => {
    if (!confirm(`Delete ${a.make} ${a.model}?`)) return;
    try {
      await api.delete(`/admin/acquisitions/${a.id}`);
      load();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div data-testid="admin-acquisitions">
      <PageHeader title="Recently Acquired" subtitle="Manually Curated" />

      <Card className="p-5 mb-6">
        <div className="qd-overline mb-4">Publish New Acquisition</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <Field label="Make" testid="acq-make" value={form.make} onChange={(v) => update("make", v)} placeholder="Hyundai" />
          <Field label="Model" testid="acq-model" value={form.model} onChange={(v) => update("model", v)} placeholder="Creta" />
          <Field label="Variant" testid="acq-variant" value={form.variant} onChange={(v) => update("variant", v)} placeholder="SX(O)" />
          <Field label="Year" testid="acq-year" value={form.year} onChange={(v) => update("year", v.replace(/\D/g, "").slice(0, 4))} placeholder="2021" />
          <Field label="Location" testid="acq-location" value={form.location} onChange={(v) => update("location", v)} placeholder="Jubilee Hills" />
          <Field label="Acquired On" testid="acq-date" value={form.acquired_on} onChange={(v) => update("acquired_on", v)} placeholder="last week / 2026-02-10" />
          <div className="col-span-2 md:col-span-2">
            <Field label="Note (Optional)" testid="acq-note" value={form.note} onChange={(v) => update("note", v)} placeholder="e.g. Single-owner, full service history" />
          </div>
          <div className="col-span-2 md:col-span-4">
            <Field label="Image URL (Optional)" testid="acq-image" value={form.image_url} onChange={(v) => update("image_url", v)} placeholder="https://… (16:9 ratio looks best)" />
          </div>
        </div>
        {form.image_url && (
          <div className="mb-3">
            <div className="text-[10px] tracking-wide text-white/45 mb-1.5">Preview</div>
            <div className="w-full max-w-xs aspect-video bg-[#0E0E0E] border border-[#1F1F1F] overflow-hidden">
              <img src={form.image_url} alt="preview" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = "0.2"; }} />
            </div>
          </div>
        )}
        <button
          data-testid="acq-create-btn"
          onClick={create}
          disabled={saving}
          className="btn-primary text-xs inline-flex items-center gap-2"
        >
          <Plus size={13} weight="bold" /> {saving ? "Publishing…" : "Publish"}
        </button>
      </Card>

      <Card className="p-5">
        <div className="qd-overline mb-4">Feed</div>
        {loading && <div className="text-white/40 text-sm">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="text-white/45 text-sm">No acquisitions yet. Publish your first above.</div>
        )}
        <div className="space-y-2">
          {items.map((a) => (
            <div key={a.id} className="flex items-center gap-3 px-4 py-3 border border-[#1F1F1F] bg-[#0E0E0E]">
              {a.image_url ? (
                <div className="w-16 h-12 bg-[#1A1A1A] overflow-hidden shrink-0">
                  <img src={a.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = "0.2"; }} />
                </div>
              ) : (
                <div className="w-16 h-12 bg-[#1A1A1A] flex items-center justify-center text-[9px] text-white/30 shrink-0">no img</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <div className="font-display text-sm text-white truncate">
                    {a.year ? `${a.year} ` : ""}{a.make} {a.model}{a.variant ? ` ${a.variant}` : ""}
                  </div>
                  {!a.published && (
                    <span className="text-[9px] tracking-wide text-white/50 px-1.5 py-[1px] border border-white/15">DRAFT</span>
                  )}
                </div>
                <div className="text-[11px] text-white/50">
                  {a.location && <>{a.location} · </>}
                  {a.acquired_on || timeAgo(a.created_at)}
                  {a.note && <> · {a.note}</>}
                </div>
              </div>
              <button
                data-testid={`acq-toggle-${a.id}`}
                onClick={() => togglePublish(a)}
                className="text-white/55 hover:text-white p-2"
                title={a.published ? "Unpublish" : "Publish"}
              >
                {a.published ? <Eye size={14} weight="bold" /> : <EyeSlash size={14} weight="bold" />}
              </button>
              <button
                data-testid={`acq-delete-${a.id}`}
                onClick={() => remove(a)}
                className="text-white/55 hover:text-[#FF1E1E] p-2"
                title="Delete"
              >
                <Trash size={14} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="block text-[10px] tracking-wide text-white/45 mb-1.5">{label}</label>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-[#0F0F0F] border border-[#2A2A2A] text-white text-xs px-3 py-2 outline-none focus:border-[#FF1E1E]"
      />
    </div>
  );
}
